import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {AnalysisChartVisualizationConfig} from 'analysis-tool/charting/AnalysisChartingTypes';
import getPersistedVisualizationConfig from 'analysis-tool/ui/charting/getPersistedVisualizationConfig';
import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import isSystemColumn from 'data-frames/util/isSystemColumn';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AnalyticsVisualizationLegendLocation as LegendLocation} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VISUALIZATION_TYPE_SETTERS} from 'insights/analytics-visualizations/data/VisualizationTypeSetters';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {MakeAllFieldsOptional} from 'util/Types';
import uniqueId from 'util/uniqueId';

type DraftRegressionConfig = MakeAllFieldsOptional<
  PipelineStepConfig<PipelineStepType.REGRESSION, 0>['regressionConfig']
>;

/**
 * Get the visualization config that should be displayed in the editor component.
 *
 * `nullableVisualizationConfig` comes from the global client state and is never explicitly set to null, so it will
 * only be null when the component is first rendered. Therefore, at first render, we return the default visualization
 * config parameters, overwriting any default parameters that may be defined by the regression step's persisted
 * regression config.
 *
 */
export function getDefaultVisualizationConfigFromRegression(
  regressionStep: GetAnalysisPipelineStep,
  nullableVisualizationConfig: VisualizationConfig | null
): VisualizationConfig {
  const DEFAULT_VISUALIZATION_CONFIG_GQL = getDefaultVisualizationConfigGQL(
    regressionStep.inputStepDatas[0].dataFrames[0].allColumnsJson,
    VisualizationType.SCATTER_PLOT
  );

  const persistedRegressionConfig: DraftRegressionConfig | null =
    regressionStep.config?.variables.find((variable) => variable.name === 'regressionConfig')?.value ?? null;

  return (
    nullableVisualizationConfig ?? {
      ...DEFAULT_VISUALIZATION_CONFIG_GQL,
      parameters: {
        ...DEFAULT_VISUALIZATION_CONFIG_GQL.parameters,
        ...(persistedRegressionConfig != null
          ? {
              ...(persistedRegressionConfig.xColumn != null
                ? {xAxisColumnName: persistedRegressionConfig.xColumn}
                : {}),
              ...(persistedRegressionConfig.yColumn != null
                ? {yAxisColumnName: persistedRegressionConfig.yColumn}
                : {}),
              ...(persistedRegressionConfig.partitionColumn != null
                ? {
                    seriesColumnName: persistedRegressionConfig.partitionColumn,
                    legendLocation: LegendLocation.TOP,
                  }
                : {}),
            }
          : {}),
      },
    }
  );
}

export function getDefaultVisualizationConfigGQL(
  dataFrameColumns: DataFrameColumns,
  visualizationType: VisualizationType
): VisualizationConfig {
  const starterConfig: VisualizationConfig = {
    __typename: 'AnalyticsVisualizationNode',
    id: `stub-id-${uniqueId()}`,
    parameters: {
      __typename: 'AnalyticsVisualizationTableParametersNode',
      displayColumnsJson: null,
    },
  };

  return {
    ...starterConfig,
    ...new VISUALIZATION_TYPE_SETTERS[visualizationType](
      dataFrameColumns.filter((column) => !isSystemColumn(column)),
      starterConfig,
      true
    ).getUpdate(),
  };
}

export default function getDefaultVisualizationConfig(
  dataFrameColumns: DataFrameColumns,
  visualizationType: VisualizationType
): AnalysisChartVisualizationConfig {
  return getPersistedVisualizationConfig(
    getDefaultVisualizationConfigGQL(dataFrameColumns, visualizationType)
  );
}
